import { useMemo, useState } from "react";
import { Edit, PackageCheck, Plus, Power, Trash2 } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { parseMoneyBR } from "@/lib/money";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiList } from "@/lib/api";
import {
  defaultModulosSaas,
  ModuloCliente,
  ModuloClienteStatus,
  ModuloEscopo,
  ModuloRegraCobranca,
  ModuloSaas,
  useModulosSaas,
} from "@/hooks/useModulosSaas";
import { useEffect } from "react";
import { todayLocalISO } from "@/lib/date-utils";

type ClienteSaas = {
  id: string;
  nome: string;
  email_responsavel?: string;
  status_assinatura?: string;
};

type ClinicaSaas = {
  id: string;
  cliente_id: string;
  nome: string;
};

const emptyModulo: Omit<ModuloSaas, "id"> = {
  chave: "",
  nome: "",
  descricao: "",
  categoria: "Geral",
  status: "Ativo",
  escopo: "cliente",
  regra_cobranca: "mensal",
  valor_mensal: 0,
  bloqueio_ativo: false,
  ordem: 100,
};

const emptyLiberacao: Omit<ModuloCliente, "id" | "modulo_id"> = {
  modulo_chave: "",
  cliente_id: "",
  clinica_id: "",
  status: "Ativo",
  escopo_aplicado: "cliente",
  valor_mensal: 0,
  inicio: todayLocalISO(),
  fim: "",
  observacoes: "",
};

const money = (value?: number) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const normalizeKey = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const badgeVariant = (status?: string) => {
  if (status === "Ativo" || status === "Teste") return "default";
  if (status === "Bloqueado" || status === "Cancelado" || status === "Inativo") return "destructive";
  return "secondary";
};

export function ModulosSaas() {
  const { toast } = useToast();
  const {
    modulos,
    liberacoes,
    loading,
    upsertModulo,
    upsertLiberacao,
    removeModulo,
    removeLiberacao,
    seedDefaultModulos,
  } = useModulosSaas();
  const [clientes, setClientes] = useState<ClienteSaas[]>([]);
  const [clinicas, setClinicas] = useState<ClinicaSaas[]>([]);
  const [moduloDialog, setModuloDialog] = useState(false);
  const [liberacaoDialog, setLiberacaoDialog] = useState(false);
  const [editingModulo, setEditingModulo] = useState<ModuloSaas | null>(null);
  const [editingLiberacao, setEditingLiberacao] = useState<ModuloCliente | null>(null);
  const [moduloForm, setModuloForm] = useState(emptyModulo);
  const [liberacaoForm, setLiberacaoForm] = useState(emptyLiberacao);

  useEffect(() => {
    Promise.all([apiList<ClienteSaas>("clientes_saas"), apiList<ClinicaSaas>("clinicas")])
      .then(([clientesRows, clinicasRows]) => {
        setClientes(clientesRows);
        setClinicas(clinicasRows);
      })
      .catch(() => {
        setClientes([]);
        setClinicas([]);
      });
  }, []);

  const metricas = useMemo(() => {
    return {
      catalogo: modulos.length,
      bloqueantes: modulos.filter((item) => item.bloqueio_ativo).length,
      ativos: liberacoes.filter((item) => item.status === "Ativo" || item.status === "Teste").length,
      solicitados: liberacoes.filter((item) => item.status === "Solicitado").length,
    };
  }, [liberacoes, modulos]);

  const openModulo = (modulo?: ModuloSaas) => {
    setEditingModulo(modulo || null);
    setModuloForm(modulo ? { ...modulo } : { ...emptyModulo });
    setModuloDialog(true);
  };

  const openLiberacao = (liberacao?: ModuloCliente, modulo?: ModuloSaas, clienteId?: string) => {
    const selectedModulo = modulo || modulos.find((item) => item.chave === liberacao?.modulo_chave) || modulos[0];
    setEditingLiberacao(liberacao || null);
    setLiberacaoForm(
      liberacao
        ? { ...liberacao }
        : {
            ...emptyLiberacao,
            modulo_chave: selectedModulo?.chave || "",
            cliente_id: clienteId || clientes[0]?.id || "",
            escopo_aplicado: selectedModulo?.escopo || "cliente",
            valor_mensal: selectedModulo?.valor_mensal || 0,
          }
    );
    setLiberacaoDialog(true);
  };

  const saveModulo = async () => {
    const chave = normalizeKey(moduloForm.chave || moduloForm.nome);
    if (!chave || !moduloForm.nome.trim()) {
      toast({ title: "Informe nome e chave do módulo", variant: "destructive" });
      return;
    }
    await upsertModulo({ ...moduloForm, id: editingModulo?.id, chave, valor_mensal: parseMoneyBR(moduloForm.valor_mensal) });
    toast({ title: "Módulo salvo" });
    setModuloDialog(false);
  };

  const saveLiberacao = async () => {
    if (!liberacaoForm.modulo_chave || !liberacaoForm.cliente_id) {
      toast({ title: "Selecione módulo e cliente", variant: "destructive" });
      return;
    }
    await upsertLiberacao({
      ...liberacaoForm,
      id: editingLiberacao?.id,
      valor_mensal: parseMoneyBR(liberacaoForm.valor_mensal),
      clinica_id: liberacaoForm.escopo_aplicado === "clinica" ? liberacaoForm.clinica_id : "",
    });
    toast({ title: "Liberação salva" });
    setLiberacaoDialog(false);
  };

  const seed = async () => {
    await seedDefaultModulos();
    toast({ title: "Catálogo inicial criado", description: `${defaultModulosSaas.length} módulos base foram conferidos.` });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Central de Módulos SaaS</h1>
            <p className="text-sm text-muted-foreground">Configure o catálogo comercial e libere módulos por cliente ou unidade.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={seed}>
              <PackageCheck className="mr-2 h-4 w-4" />
              Criar catálogo base
            </Button>
            <Button onClick={() => openModulo()}>
              <Plus className="mr-2 h-4 w-4" />
              Módulo
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Catálogo</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{metricas.catalogo}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Com bloqueio</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{metricas.bloqueantes}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Liberados</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{metricas.ativos}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Solicitados</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{metricas.solicitados}</CardContent></Card>
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr]">
          <Card>
            <CardHeader><CardTitle>Catálogo de módulos</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {loading && <p className="text-sm text-muted-foreground">Carregando módulos...</p>}
              {!loading && modulos.length === 0 && <p className="text-sm text-muted-foreground">Nenhum módulo cadastrado. Use o catálogo base para começar.</p>}
              {modulos.map((modulo) => (
                <div key={modulo.id} className="space-y-3 rounded-md border p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium">{modulo.nome}</p>
                        <Badge variant={badgeVariant(modulo.status)}>{modulo.status}</Badge>
                        {modulo.bloqueio_ativo && <Badge variant="secondary">bloqueia menu</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">{modulo.descricao || "Sem descrição"}</p>
                      <p className="text-xs text-muted-foreground">
                        {modulo.chave} - {modulo.categoria || "Geral"} - {modulo.escopo} - {modulo.regra_cobranca} - {money(modulo.valor_mensal)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openLiberacao(undefined, modulo)}>
                        <Power className="mr-1 h-3 w-3" /> Liberar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openModulo(modulo)}><Edit className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => removeModulo(modulo.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Liberações e solicitações</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full" variant="outline" onClick={() => openLiberacao()} disabled={modulos.length === 0 || clientes.length === 0}>
                <Plus className="mr-2 h-4 w-4" />
                Nova liberação manual
              </Button>
              {liberacoes.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma liberação ou solicitação registrada.</p>}
              {liberacoes.map((liberacao) => {
                const cliente = clientes.find((item) => item.id === liberacao.cliente_id);
                const modulo = modulos.find((item) => item.chave === liberacao.modulo_chave);
                const clinica = clinicas.find((item) => item.id === liberacao.clinica_id);
                return (
                  <div key={liberacao.id} className="rounded-md border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{modulo?.nome || liberacao.modulo_chave}</p>
                        <p className="text-sm text-muted-foreground">{cliente?.nome || liberacao.cliente_id}</p>
                        <p className="text-xs text-muted-foreground">
                          {liberacao.escopo_aplicado === "clinica" ? clinica?.nome || "Clínica específica" : "Conta toda"} - {money(liberacao.valor_mensal)}
                        </p>
                      </div>
                      <Badge variant={badgeVariant(liberacao.status)}>{liberacao.status}</Badge>
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openLiberacao(liberacao)}><Edit className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => removeLiberacao(liberacao.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={moduloDialog} onOpenChange={setModuloDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingModulo ? "Editar módulo" : "Novo módulo"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={moduloForm.nome} onChange={(event) => setModuloForm({ ...moduloForm, nome: event.target.value, chave: moduloForm.chave || normalizeKey(event.target.value) })} />
            </div>
            <div className="space-y-2">
              <Label>Chave técnica</Label>
              <Input value={moduloForm.chave} onChange={(event) => setModuloForm({ ...moduloForm, chave: normalizeKey(event.target.value) })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Descrição</Label>
              <Textarea value={moduloForm.descricao || ""} onChange={(event) => setModuloForm({ ...moduloForm, descricao: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Input value={moduloForm.categoria || ""} onChange={(event) => setModuloForm({ ...moduloForm, categoria: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Valor mensal</Label>
              <MoneyInput value={moduloForm.valor_mensal || 0} onValueChange={(valor_mensal) => setModuloForm({ ...moduloForm, valor_mensal })} />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={moduloForm.status} onValueChange={(status: "Ativo" | "Inativo") => setModuloForm({ ...moduloForm, status })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Ativo">Ativo</SelectItem><SelectItem value="Inativo">Inativo</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cobrança</Label>
              <Select value={moduloForm.regra_cobranca} onValueChange={(regra_cobranca: ModuloRegraCobranca) => setModuloForm({ ...moduloForm, regra_cobranca })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gratuito">Gratuito</SelectItem>
                  <SelectItem value="mensal">Mensal</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="plano">Incluso no plano</SelectItem>
                  <SelectItem value="teste">Teste</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Escopo</Label>
              <Select value={moduloForm.escopo} onValueChange={(escopo: ModuloEscopo) => setModuloForm({ ...moduloForm, escopo })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cliente">Conta toda</SelectItem><SelectItem value="clinica">Por clínica</SelectItem></SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={!!moduloForm.bloqueio_ativo} onCheckedChange={(checked) => setModuloForm({ ...moduloForm, bloqueio_ativo: checked === true })} />
              Bloquear menu/rota quando não liberado
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setModuloDialog(false)}>Cancelar</Button>
            <Button onClick={saveModulo}>Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={liberacaoDialog} onOpenChange={setLiberacaoDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingLiberacao ? "Editar liberação" : "Liberar módulo"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Módulo</Label>
              <Select value={liberacaoForm.modulo_chave} onValueChange={(modulo_chave) => {
                const modulo = modulos.find((item) => item.chave === modulo_chave);
                setLiberacaoForm({ ...liberacaoForm, modulo_chave, escopo_aplicado: modulo?.escopo || "cliente", valor_mensal: modulo?.valor_mensal || 0 });
              }}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{modulos.map((modulo) => <SelectItem key={modulo.id} value={modulo.chave}>{modulo.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cliente</Label>
              <Select value={liberacaoForm.cliente_id} onValueChange={(cliente_id) => setLiberacaoForm({ ...liberacaoForm, cliente_id, clinica_id: "" })}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{clientes.map((cliente) => <SelectItem key={cliente.id} value={cliente.id}>{cliente.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={liberacaoForm.status} onValueChange={(status: ModuloClienteStatus) => setLiberacaoForm({ ...liberacaoForm, status })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Solicitado">Solicitado</SelectItem>
                  <SelectItem value="Teste">Teste</SelectItem>
                  <SelectItem value="Bloqueado">Bloqueado</SelectItem>
                  <SelectItem value="Cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Escopo</Label>
              <Select value={liberacaoForm.escopo_aplicado} onValueChange={(escopo_aplicado: ModuloEscopo) => setLiberacaoForm({ ...liberacaoForm, escopo_aplicado })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cliente">Conta toda</SelectItem><SelectItem value="clinica">Clínica específica</SelectItem></SelectContent>
              </Select>
            </div>
            {liberacaoForm.escopo_aplicado === "clinica" && (
              <div className="space-y-2 md:col-span-2">
                <Label>Clínica</Label>
                <Select value={liberacaoForm.clinica_id || ""} onValueChange={(clinica_id) => setLiberacaoForm({ ...liberacaoForm, clinica_id })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{clinicas.filter((clinica) => clinica.cliente_id === liberacaoForm.cliente_id).map((clinica) => <SelectItem key={clinica.id} value={clinica.id}>{clinica.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Valor mensal</Label>
              <MoneyInput value={liberacaoForm.valor_mensal || 0} onValueChange={(valor_mensal) => setLiberacaoForm({ ...liberacaoForm, valor_mensal })} />
            </div>
            <div className="space-y-2">
              <Label>Início</Label>
              <Input type="date" value={liberacaoForm.inicio || ""} onChange={(event) => setLiberacaoForm({ ...liberacaoForm, inicio: event.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Observações</Label>
              <Textarea value={liberacaoForm.observacoes || ""} onChange={(event) => setLiberacaoForm({ ...liberacaoForm, observacoes: event.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setLiberacaoDialog(false)}>Cancelar</Button>
            <Button onClick={saveLiberacao}>Salvar liberação</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
